import useRegisteredModuleFilters from '../../moduleFilters';
import React, { useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { ConfirmActionDialog, PageActionRow, PageDivider, PageFrame, PrimaryActionButton } from '../../../shared/components/patterns';
import { toast } from '../../../shared/hooks/use-toast';
import { useLinkedEntityCards } from '../../../shared/components/linked-entities/LinkedEntityCardHost';
import {
  InProgressHeader,
  InProgressStats,
  InProgressBoard,
  InProgressDetailDialog,
  CreateInProgressDealDialog,
} from '@/modules/deals/components/inProgress';
import DealsFiltersBar from '@/modules/deals/components/shared/DealsFiltersBar';
import { useInProgressDealsData } from '@/modules/deals/hooks';
import '../styles/deals.css';

const DealsInProgressPage = () => {
  const { capabilities } = useSession();
  const { openEntityCard } = useLinkedEntityCards();
  const {
    dealStages,
    deals,
    stats,
    dealsModuleDisabled,
    dealsError,
    dealsTenantDatabaseBlocked,
    selectedDeal,
    isDetailDialogOpen,
    isCreateDialogOpen,
    staff,
    staffUnavailable,
    staffCatalogStatus,
    clients,
    activeClientFilterLabel,
    isClientFiltered,
    filters,
    activeOverviewFilter,
    assigneeOptions,
    hasActiveFilters,
    setSearchTerm,
    setUrgencyFilter,
    setAssigneeFilter,
    setStageFilter,
    setClientFilter,
    clearFilters,
    setOverviewFilter,
    openDealDetails,
    closeDealDetails,
    openCreateDialog,
    closeCreateDialog,
    handleCreateDeal,
    handleSaveDeal,
    handleCancelDeal,
    handleReturnToProposal,
    handleCompleteDeal,
    completionStates,
    onDragEnd,
  } = useInProgressDealsData();
  const moduleFilters = useRegisteredModuleFilters('activeDeals', deals);
  const [cancelRequest, setCancelRequest] = useState(null);
  const [returnRequest, setReturnRequest] = useState(null);
  const [returnState, setReturnState] = useState({ loading: false, error: '' });

  function openClientDetail(clientId) {
    openEntityCard({ entityType: 'client', entityId: clientId });
  }

  function openResponsibleStaff(staffId, staffMember) {
    const resolvedId = staffId || staffMember?.id;
    if (resolvedId) openEntityCard({ entityType: 'staff', entityId: resolvedId });
  }

  return (
    <PageFrame filterModel={moduleFilters} className="deals-page">
      {!dealsModuleDisabled ? <InProgressHeader onCreateDeal={openCreateDialog} /> : null}

      {dealsModuleDisabled ? (
        <Alert className="deals-alert">
          <AlertDescription>
            {dealsError || 'Modulo affari non abilitato per questo workspace.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {!dealsModuleDisabled && dealsTenantDatabaseBlocked ? (
        <Alert className="deals-alert">
          <AlertDescription>
            {dealsError || 'Database tenant non disponibile per il modulo affari.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {!dealsModuleDisabled && !dealsTenantDatabaseBlocked && isClientFiltered ? (
        <Alert variant="info" className="deals-alert deals-alert--client">
          <AlertDescription>
            Vista filtrata per cliente: {activeClientFilterLabel}.
          </AlertDescription>
        </Alert>
      ) : null}

      {!dealsModuleDisabled && !dealsTenantDatabaseBlocked ? (
        <InProgressStats
          stats={stats}
          activeMetric={activeOverviewFilter}
          onMetricClick={setOverviewFilter}
        />
      ) : null}

      {!dealsModuleDisabled && !dealsTenantDatabaseBlocked ? (
        <>
        <PageDivider />
        <DealsFiltersBar
          searchValue={filters.searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Cerca per nome, cliente, responsabile..."
          hasActiveFilters={hasActiveFilters}
          onReset={clearFilters}
          filters={[
            {
              id: 'urgency',
              value: filters.urgencyFilter,
              onChange: setUrgencyFilter,
              placeholder: 'Priorità',
              options: [
                { value: 'all', label: 'Tutte le priorità' },
                { value: 'alta', label: 'Alta' },
                { value: 'media', label: 'Media' },
                { value: 'bassa', label: 'Bassa' },
              ],
            },
            {
              id: 'client',
              value: filters.clientFilter,
              onChange: setClientFilter,
              placeholder: 'Cliente',
              options: [
                { value: 'all', label: 'Tutti i clienti' },
                ...clients.map((client) => ({
                  value: String(client.id),
                  label: client.company || client.businessName || client.name,
                })),
              ],
            },
            {
              id: 'assignee',
              value: filters.assigneeFilter,
              onChange: setAssigneeFilter,
              placeholder: 'Responsabile',
              options: [
                { value: 'all', label: 'Tutti i responsabili' },
                ...assigneeOptions.map((assignee) => ({
                  value: assignee,
                  label: assignee,
                })),
              ],
            },
            {
              id: 'stage',
              value: filters.stageFilter,
              onChange: setStageFilter,
              placeholder: 'Fase',
              options: [
                { value: 'all', label: 'Tutte le fasi' },
                ...dealStages.map((stage) => ({
                  value: stage,
                  label: stage,
                })),
              ],
            },
          ]}
        />
        <PageDivider />
        <PageActionRow>
          <PrimaryActionButton onClick={openCreateDialog}>Nuovo</PrimaryActionButton>
        </PageActionRow>
        </>
      ) : null}

      {!dealsModuleDisabled && !dealsTenantDatabaseBlocked ? (
        <InProgressBoard
          dealStages={dealStages}
          deals={moduleFilters.filterRows(deals)}
          onCardClick={openDealDetails}
          onOpenClient={openClientDetail}
          onOpenStaff={openResponsibleStaff}
          onDragEnd={onDragEnd}
          staff={staff}
        />
      ) : null}

      {!dealsModuleDisabled && !dealsTenantDatabaseBlocked && staffUnavailable ? (
        <Alert className="deals-alert">
          <AlertDescription>
            Catalogo staff non disponibile dal backend. Le selezioni personale sono momentaneamente vuote.
          </AlertDescription>
        </Alert>
      ) : null}

      {!dealsModuleDisabled && !dealsTenantDatabaseBlocked ? (
        <InProgressDetailDialog
          deal={selectedDeal}
          isOpen={isDetailDialogOpen}
          onClose={closeDealDetails}
          onSave={handleSaveDeal}
          onComplete={handleCompleteDeal}
          completionState={selectedDeal ? completionStates[String(selectedDeal.id)] || {} : {}}
          onCancel={async (dealId) => {
            setCancelRequest({ id: dealId });
          }}
          onReturnToProposal={(dealId) => {
            setReturnState({ loading: false, error: '' });
            setReturnRequest({ id: dealId });
          }}
          onOpenClient={openClientDetail}
          onOpenStaff={openResponsibleStaff}
          staff={staff}
          clients={clients}
          canManageDealDestructiveActions={capabilities?.canManageDealDestructiveActions === true}
        />
      ) : null}

      {!dealsModuleDisabled && !dealsTenantDatabaseBlocked ? (
        <CreateInProgressDealDialog
          isOpen={isCreateDialogOpen}
          onClose={closeCreateDialog}
          onCreate={handleCreateDeal}
          stages={dealStages}
          staff={staff}
          staffCatalogStatus={staffCatalogStatus}
          clients={clients}
        />
      ) : null}

      <ConfirmActionDialog
        open={Boolean(cancelRequest)}
        title="Annullare l'affare in corso?"
        description="L'affare verrà annullato e rimosso dalla vista operativa."
        confirmLabel="Annulla affare"
        cancelLabel="Indietro"
        tone="danger"
        onCancel={() => setCancelRequest(null)}
        onConfirm={async () => {
          const result = await handleCancelDeal(cancelRequest.id);
          setCancelRequest(null);
          toast({
            title: result.ok ? 'Affare annullato' : 'Annullamento non riuscito',
            description: result.message,
            variant: result.ok ? 'default' : 'destructive',
          });
        }}
      />
      <ConfirmActionDialog
        open={Boolean(returnRequest)}
        title="Riportare l'affare alle proposte?"
        description={returnState.error || "La proposta originale verrà aggiornata con i dati più recenti e l'affare uscirà dalla vista operativa."}
        confirmLabel="Riporta nelle proposte"
        cancelLabel="Indietro"
        loading={returnState.loading}
        onCancel={() => {
          setReturnRequest(null);
          setReturnState({ loading: false, error: '' });
        }}
        onConfirm={async () => {
          if (!returnRequest || returnState.loading) return;
          setReturnState({ loading: true, error: '' });
          const result = await handleReturnToProposal(returnRequest.id);
          if (result.ok) {
            setReturnRequest(null);
            setReturnState({ loading: false, error: '' });
          } else {
            setReturnState({ loading: false, error: result.message || 'Operazione non riuscita.' });
          }
          toast({
            title: result.ok ? 'Affare riportato alle proposte' : 'Operazione non riuscita',
            description: result.message,
            variant: result.ok ? 'default' : 'destructive',
          });
        }}
      />
    </PageFrame>
  );
};

export default DealsInProgressPage;
